import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Chip,
  Divider,
  Button,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Business,
  Shield,
  CalendarToday,
  Edit,
  Block,
  CheckCircle,
  LocationOn,
  ArrowBack,
} from '@mui/icons-material';
import { useCountyUser, useSuspendCountyUser, useUnsuspendCountyUser } from '../../hooks';
import { colors } from '../../theme/theme';
import { useSnackbar } from '../../contexts/SnackbarContext';

export const UserProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const userId = parseInt(id || '0');

  const { user, isLoading } = useCountyUser(userId);
  const suspendUser = useSuspendCountyUser();
  const unsuspendUser = useUnsuspendCountyUser();

  const handleSuspend = async () => {
    if (!user) return;

    try {
      await suspendUser.mutateAsync({ countyId: user.county_id, userId: user.id });
      showSnackbar('User suspended successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to suspend user', 'error');
    }
  };

  const handleUnsuspend = async () => {
    if (!user) return;

    try {
      await unsuspendUser.mutateAsync({ countyId: user.county_id, userId: user.id });
      showSnackbar('User activated successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to activate user', 'error');
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>Loading user profile...</Typography>
        </Box>
      </AdminLayout>
    );
  }

  if (!user) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography>User not found</Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="User Profile"
          subtitle="View and manage user details"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'County Users', path: '/superadmin/county-users' },
            { label: 'Profile' },
          ]}
          action={
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/superadmin/county-users')}
              >
                Back
              </Button>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => navigate(`/superadmin/county-users/${userId}/edit`)}
              >
                Edit User
              </Button>
              {user.is_active === 1 ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Block />}
                  onClick={handleSuspend}
                >
                  Suspend User
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={handleUnsuspend}
                >
                  Activate User
                </Button>
              )}
            </Stack>
          }
        />

        <Grid container spacing={3}>
          {/* Profile Overview Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${colors.grey[200]}`,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      bgcolor: colors.primary.main,
                      fontSize: '3rem',
                      mb: 2,
                    }}
                  >
                    {user.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {user.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {user.email}
                  </Typography>
                  <Chip
                    label={user.is_active === 1 ? 'Active' : 'Suspended'}
                    color={user.is_active === 1 ? 'success' : 'error'}
                    sx={{ mt: 2 }}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    User Type
                  </Typography>
                  <Chip
                    label={user.user_type}
                    size="small"
                    color="primary"
                    sx={{ textTransform: 'capitalize', mb: 2 }}
                  />
                </Box>

                {user.roles && user.roles.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Roles
                    </Typography>
                    <Stack direction="column" spacing={1}>
                      {user.roles.map((role) => (
                        <Chip
                          key={role.id}
                          icon={<Shield sx={{ fontSize: 16 }} />}
                          label={role.name}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Detailed Information Card */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${colors.grey[200]}`,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  User Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: colors.grey[50],
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Person sx={{ color: colors.primary.main }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            First Name
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {user.first_name || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: colors.grey[50],
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Person sx={{ color: colors.primary.main }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Last Name
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {user.last_name || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: colors.grey[50],
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Email sx={{ color: colors.primary.main }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Email Address
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: colors.grey[50],
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Business sx={{ color: colors.primary.main }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            County
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {user.county?.name || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: colors.grey[50],
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <CalendarToday sx={{ color: colors.primary.main }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Created At
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {new Date(user.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: colors.grey[50],
                        borderRadius: 2,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <CalendarToday sx={{ color: colors.primary.main }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Last Updated
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {new Date(user.updated_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                {user.permissions && user.permissions.length > 0 && (
                  <>
                    <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 4 }}>
                      Permissions
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {user.permissions.map((permission) => (
                        <Chip
                          key={permission.id}
                          label={permission.name}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </AdminLayout>
  );
};
